export interface FieldMapping {
  id: string;
  crmField: string;
  crmFieldLabel: string;
  tpaField: string;
  tpaFieldLabel: string;
  direction: 'crm_to_tpa' | 'tpa_to_crm' | 'bidirectional';
  active: boolean;
}

/**
 * Outbound forwarding webhook — user configures URLs to receive
 * real-time copies of inbox events (incoming messages, status updates,
 * etc.). Mirrors the legacy `FORWARDING_WEBHOOKS_LIST` schema in
 * `firebase-things.js:21056-21105` so the CF that fans events out the
 * webhooks (server-side) keeps reading the same shape — field names
 * are deliberately snake_case to match the legacy doc.
 *
 * Stored as a top-level field `FORWARDING_WEBHOOKS_LIST` on the
 * personal preferences doc (NOT nested under `ua_app_preferences`),
 * keyed by `id`. WhatsApp-only feature today; other TPAs hide the
 * card via the `getVisibleSettingsCards` opt-in.
 */
export interface ForwardingWebhook {
  /** Stable identifier — legacy used `Date.now()` as a number; we
   *  store as string to dodge JS precision quirks. */
  id: string;
  name: string;
  webhook_url: string;
  state: boolean;
  /** Always 'POST' today; kept as a field for legacy compatibility
   *  (the CF reads it). */
  method: 'POST';
  /** Always 'application/json' today; same reasoning. */
  contentType: 'application/json';
  /** Either ['allEvents'] or specific event keys — legacy supports
   *  both shapes. */
  allowedEvents: string[];
}

export interface WebhookConfigSetting {
  id: string;
  name: string;
  url: string;
  events: string[];
  secret?: string;
  active: boolean;
  createdAt: Date;
  lastTriggered?: Date;
}

export interface WorkflowRule {
  id: string;
  name: string;
  trigger: WorkflowTrigger;
  conditions: WorkflowCondition[];
  actions: WorkflowAction[];
  active: boolean;
  createdAt: Date;
}

export interface WorkflowTrigger {
  type: 'incoming_message' | 'outgoing_message' | 'webhook' | 'schedule';
  config: Record<string, unknown>;
}

export interface WorkflowCondition {
  field: string;
  operator: 'equals' | 'contains' | 'starts_with' | 'ends_with' | 'regex';
  value: string;
}

export interface WorkflowAction {
  type: 'send_message' | 'create_record' | 'update_record' | 'webhook' | 'notify';
  config: Record<string, unknown>;
}

export interface CustomSender {
  id: string;
  label: string;
  senderId: string;
  channel: 'SMS' | 'WHATSAPP';
  createdAt: number;
}

/** Cached sender details stored in preferences so views can instantly apply defaults
 *  without waiting for the async getSenders() call. */
export interface DefaultSenderInfo {
  id: string;
  label: string;
  channel: string;
  phoneNumber?: string;
}

export interface AppPreferences {
  defaultChannel: string;
  /** Global default sender (used as fallback). */
  defaultSender?: DefaultSenderInfo;
  defaultCountryCode?: string;
  /** Bypass the country-code prompt entirely. For users whose contacts span
   *  multiple countries — picking a single default would mangle some of the
   *  recipients, so they'd rather send numbers as-typed. When true, the
   *  pre-send "country code required" dialog never appears and
   *  `applyCountryCode` is effectively a no-op (it already returns the input
   *  unchanged when the country code is empty). */
  multiCountryContacts?: boolean;
  hiddenSenderIds?: string[];
  customSenders?: CustomSender[];
  hideSenderPicker?: boolean;
  autoSaveRecipients: boolean;
  showDeliveryReports: boolean;
  enableNotifications: boolean;
  language: string;
  timezone: string;
  /** Delay in ms between each message in bulk send. Default: 0 (no delay) */
  bulkSendDelay?: number;
  /** Default message signature appended to outbound messages */
  messageSignature?: string;
  /** Show emoji picker button in chat input. Default: true */
  showEmojiPicker?: boolean;
  /** Media display mode in chat: 'inline' shows preview, 'link' shows download link only. Default: 'inline' */
  mediaDisplayMode?: 'inline' | 'link';
  /** Show conversation labels (Open, Closed, etc.) in chat header. Default: true */
  showLabels?: boolean;
  /** Show agent/assignee picker in chat header. Default: true */
  showAssignees?: boolean;
  /** Default sender for inbox/conversations list view (CONVERSATIONS). */
  inboxDefaultSender?: DefaultSenderInfo;
  /** Hidden senders in inbox view (separate from compose). */
  inboxHiddenSenderIds?: string[];
  /** Default sender for chat views (CHAT). */
  chatDefaultSender?: DefaultSenderInfo;
  /** Default sender for compose/message form view (MESSAGE_FORM). */
  composeDefaultSender?: DefaultSenderInfo;
  /** Show a message preview popup at the bottom when a new incoming message arrives outside the inbox. Default: true */
  showIncomingPreview?: boolean;

  /**
   * Modules (or Monday board IDs) that the conversation-list record-
   * enrichment looks up against — capped at 2. Per-user, top priority
   * over the default fallback (which picks the first 2 supported modules
   * excluding Tickets / Accounts / Companies / Organizations and skips
   * Monday entirely). Configured under Inbox View Settings → Conversation
   * List Record Lookup.
   */
  inboxRecordEnrichmentModules?: string[];

  // ── Chat View Settings ──
  /** Chat message font size. Default: 'normal' */
  chatFontSize?: 'small' | 'normal' | 'large';
  /** Show timestamps on every message or only on hover. Default: 'always' */
  chatTimestampDisplay?: 'always' | 'hover';
  /** Timestamp format. Default: '12h' */
  chatTimeFormat?: '12h' | '24h';
  /** Enter key sends message (true) or adds newline (false). Default: true */
  chatEnterToSend?: boolean;

  // ── Dialer / Call Settings ──
  /** Default caller ID value (phone number string). */
  defaultCallerId?: string;
  /** Caller IDs hidden from the dialer dropdown. */
  hiddenCallerIds?: string[];

  // ── Onboarding ──
  /** Contact number for app notifications/alerts (preferably WhatsApp) */
  notificationContact?: string;
  /** True once the user has completed the onboarding wizard */
  onboardingComplete?: boolean;
  /** Hide the "incoming sync not configured" amber alert permanently */
  hideIncomingSyncAlert?: boolean;
  /** When false, incoming messages are NOT recorded in SaaS/CRM. Default: true */
  recordInSaasEnabled?: boolean;
  /** Custom order for kanban columns — array of label IDs. Any labels not in this list are appended. */
  kanbanLabelOrder?: string[];
  /** Label IDs hidden from the kanban view */
  kanbanHiddenLabels?: string[];
}

// ── Team & Permission Types ─────────────────────────────────────────

export type SenderPermissionLevel = 'no_access' | 'view_only' | 'view_and_send' | 'full_access';
export type LabelPermissionLevel = 'no_access' | 'view_only' | 'view_and_apply' | 'full_access';
export type AssigneePermissionLevel = 'no_access' | 'view_only' | 'self_assign_only' | 'full_access';

export type PermissionCategoryKey = 'senders' | 'labels' | 'assignees';

/** The selectable levels per category — single source of truth.
 *  Both the editor dropdowns (CATEGORY_CONFIG) and the resolver's validity
 *  check derive from this. Kept together on purpose: a level offered in the UI
 *  but unknown to the resolver would be stored and then silently treated as
 *  "unset", i.e. fail open. */
export const PERMISSION_LEVELS: {
  senders: readonly SenderPermissionLevel[];
  labels: readonly LabelPermissionLevel[];
  assignees: readonly AssigneePermissionLevel[];
} = {
  senders: ['no_access', 'view_only', 'view_and_send', 'full_access'],
  labels: ['no_access', 'view_only', 'view_and_apply', 'full_access'],
  assignees: ['no_access', 'view_only', 'self_assign_only', 'full_access'],
};

/** Levels the editor OFFERS. Deliberately narrower than PERMISSION_LEVELS for
 *  senders: 'full_access' grants nothing beyond 'view_and_send' for a number
 *  (every consumer checks `view_and_send || full_access` and nothing checks
 *  full_access alone), so offering it only makes the screen look broken when
 *  picking it changes nothing. The legacy app hid the same option for senders.
 *
 *  Values ALREADY stored stay valid — PERMISSION_LEVELS is the validity
 *  contract, and PermissionCategory still renders a stored level that is no
 *  longer offered so it displays and can be changed. */
export const SELECTABLE_PERMISSION_LEVELS: {
  senders: readonly SenderPermissionLevel[];
  labels: readonly LabelPermissionLevel[];
  assignees: readonly AssigneePermissionLevel[];
} = {
  senders: PERMISSION_LEVELS.senders.filter((l) => l !== 'full_access'),
  // 'full_access' ("Manage Labels") is no longer offered per-label: label
  // management (add/edit) is now a single group-wide toggle
  // (labels.global.users_can_manage_labels), so the per-label dropdown covers
  // only Hide / View Only / View & Apply. Any full_access ALREADY stored stays
  // valid (PERMISSION_LEVELS is the contract) and still renders + grants manage
  // for back-compat — same treatment as senders' hidden full_access.
  labels: PERMISSION_LEVELS.labels.filter((l) => l !== 'full_access'),
  assignees: PERMISSION_LEVELS.assignees,
};

export interface CategoryPermission<TLevel extends string> {
  /** Blanket level applied to every item in this category. Optional on purpose:
   *  a group only carries one when an admin explicitly picked it. When absent,
   *  resolution falls through to the _default group instead of being shadowed
   *  by a value nobody chose. */
  default?: TLevel;
  /** Whole-category flags. Typed so readers don't cast: `users_can_manage_labels`
   *  and `users_can_delete_labels` are the labels "Manage Labels" / "Delete Labels"
   *  toggles (see TeamPermissions.labels); other categories store nothing here. */
  global: { users_can_manage_labels?: boolean; users_can_delete_labels?: boolean } & Record<string, unknown>;
  [specificItemId: string]: TLevel | Record<string, unknown> | undefined;
}

export interface TeamPermissions {
  senders?: CategoryPermission<SenderPermissionLevel>;
  /** Per-label Hide/View/Apply live in `default` + the specific-item keys.
   *  `global.users_can_manage_labels` (boolean) is the group-wide "Manage
   *  Labels" toggle — grants creating labels (the app has no rename/edit). When
   *  unset, management falls back to the legacy signal (a 'full_access' category
   *  default). `global.users_can_delete_labels` (boolean) is the separate
   *  "Delete Labels" toggle — OFF unless an admin turns it on (no legacy
   *  fallback); delete also needs View & Apply on that label. */
  labels?: CategoryPermission<LabelPermissionLevel>;
  assignees?: CategoryPermission<AssigneePermissionLevel>;
  inbox?: { global: { users_can_view_other_users_chats: boolean; users_can_view_unassigned_chats: boolean } };
  calls?: { global: { users_can_only_view_allowed_senders_calls_and_logs: boolean; users_can_use_whatsapp_calling: boolean; users_can_make_outbound_calls?: boolean; users_can_view_recordings?: boolean; hidden_caller_ids?: string[] } };
}

export interface TeamMember {
  name: string;
  email: string;
  picture?: string;
  /** Firebase Auth UID. Resolved once at add-time via fetchUserDetails and stored
   *  so later reads (e.g. server-side routing) don't need an email→uid lookup.
   *  Optional for back-compat with members added before this field existed. */
  uid?: string;
}

export interface Team {
  id: string;
  name: string;
  description: string;
  members: Record<string, TeamMember>;
  permissions: TeamPermissions;
  createdAt: string;
}

export type UsersAccessPermissions = Record<string, Team>;

export interface DebugConfig {
  enableLogging: boolean;
  logLevel: 'error' | 'warn' | 'info' | 'debug';
  showApiCalls: boolean;
}

/**
 * Incoming message capture settings.
 * Controls whether incoming SMS/WhatsApp messages are captured and synced.
 */
export interface IncomingUnreadFieldMapping {
  module_id: string;
  module_name: string;
  field_id: string;
  field_name: string;
  value?: string;
}

/** Monday.com multi-board search entry — stored in _ua_custome_search_modules_and_fields */
export interface MultiSearchBoardEntry {
  module: string;          // Board ID
  searchFieldType: string; // 'phone' | 'email'
  searchField: string;     // Column ID
}

export interface IncomingCaptureSettings {
  /** Master toggle for incoming message capture */
  enabled: boolean;
  /** Enable SMS incoming capture */
  smsEnabled: boolean;
  /** Enable WhatsApp incoming capture */
  whatsappEnabled: boolean;
  /** Auto-create CRM records for new contacts */
  autoCreateContacts: boolean;
  /** Messaging service ID (e.g., Twilio Messaging Service SID) */
  serviceSid?: string;

  /** CRM module to create records in (e.g. 'Leads', 'Contacts') */
  targetModule?: string;
  /** Upload incoming message attachments to CRM module records */
  uploadAttachmentToModule?: boolean;
  /** Enable Signal Notifications in Zoho CRM (Contacts/Leads only) */
  enableSignalNotifications?: boolean;
  /** Phone pattern: match numbers without country code prefix */
  phonePatternNoCountryCode?: boolean;
  /** Phone pattern: match numbers starting with 0 */
  phonePatternStartsWith0?: boolean;
  /** Phone pattern: match numbers starting with + */
  phonePatternStartsWithPlus?: boolean;
  /** Ignore messages from new customers (only sync existing) */
  ignoreNewCustomers?: boolean;
  /** Incoming unread field mappings — update CRM field on new message */
  incomingUnreadFieldMap?: Record<string, IncomingUnreadFieldMapping>;
  /** Default department ID for ticket creation (Zoho Desk, Freshdesk) */
  defaultDepartmentId?: string;
  /** Listen for ticket public comments to send replies (Zoho Desk) */
  ticketReplyCapture?: boolean;
  /** Sync contact name, ticket status and assignee with inbox chats (Zoho Desk) */
  fieldsUpdateSync?: boolean;
  /** Set an existing non-open ticket back to Open on a new incoming message (Freshdesk) */
  reopenExistingTicket?: boolean;

  /**
   * Sender/channel IDs to IGNORE for incoming capture.
   * All senders are captured by default; IDs in this list are excluded.
   * Used by MessageBird and other multi-channel providers.
   */
  ignoredSenderIds?: string[];

  /**
   * Twilio message status event types to IGNORE for incoming capture
   * (e.g. 'com.twilio.messaging.message.delivered'). All delivery statuses
   * are captured by default; ids in this list are excluded. Twilio only —
   * see TWILIO_MESSAGE_EVENTS.
   */
  ignoredMessageStatuses?: string[];

  /**
   * WhatsApp (WABA) statuses NOT shown in the inbox ('sent', 'delivered',
   * 'read', 'failed'). Saved as ua_whatsapp_ignored_message_statuses.
   * WhatsApp only — see WHATSAPP_MESSAGE_EVENTS.
   */
  ignoredWhatsappEvents?: string[];

  /**
   * WhatsApp (WABA) events NOT sent to forwarding webhooks ('incoming',
   * 'sent', 'delivered', 'read', 'failed'). Saved as
   * ua_whatsapp_webhook_ignored_message_statuses.
   */
  ignoredWhatsappWebhookEvents?: string[];

  /**
   * Resend a message as SMS when its WhatsApp delivery fails (Twilio only).
   * Backend reads ua_twilio_resend_sms_on_whatsapp_failure on failed/undelivered
   * status events and re-sends the original body over SMS.
   */
  resendSmsOnWhatsappFailure?: boolean;
  /**
   * Default SMS sender number (E.164) for the WhatsApp-failure fallback.
   * Empty = use the same number the WhatsApp message was sent from.
   */
  whatsappFailureSmsFallbackNumber?: string;
  /**
   * Per-WhatsApp-sender SMS number overrides for the fallback.
   * Key = WhatsApp sender digits (E.164 without `whatsapp:` or `+`),
   * value = SMS sender number (E.164). Senders without an entry use
   * whatsappFailureSmsFallbackNumber.
   */
  whatsappFailureSmsSenderMap?: Record<string, string>;

  // Monday.com–specific
  /** History board ID where incoming messages are logged as items (Monday.com "App Template Board") */
  historyModule?: string;
  /** Column ID for the phone field on the contact board (Monday.com) */
  targetPhoneField?: string;
  /** Column ID for the email field on the contact board (Monday.com) */
  targetEmailField?: string;
  /** Multi-board search config — up to 5 boards for searching existing contacts (Monday.com) */
  customSearchModules?: MultiSearchBoardEntry[];
}

/**
 * SaaS-side incoming sync config.
 * Saved at: ulgebraUsers/{uid}/extensions/whatsappforpage/incomingSyncSaas/{saasCode}_{orgId}
 * Allows the standalone whatsappforpage CF to know which SaaS to record messages into.
 */
export interface IncomingSyncSaasConfig {
  /** Master toggle — record incoming messages into SaaS */
  enabled: boolean;
  /** Target CRM module (e.g. 'Leads', 'Contacts') */
  targetModule: string;
  /** Auto-create contacts for new senders */
  autoCreateContacts: boolean;
  /** Upload attachments to SaaS module records */
  uploadAttachmentToModule: boolean;
}

/**
 * Event Incoming Capture Settings (for Calendly/Acuity)
 * Mirrors: ua_incoming_enable_capture, ua_incoming_tpa_ignored_channels from static files
 */
export interface EventIncomingCaptureSettings {
  /** Master toggle for incoming event capture */
  enabled: boolean;
  /** Event type IDs to ignore (not capture) */
  ignoredEventTypes?: string[];
  /** Automatically ignore newly added event types */
  ignoreNewEventTypes?: boolean;
  /**
   * Event type IDs that were enabled at the last save. With ignoreNewEventTypes
   * on, any type missing from this list is "new" and is skipped by the backend.
   * Undefined = never saved, so no type counts as new.
   */
  allowedEventTypes?: string[];
  /** Ignore events from new customers (only sync existing CRM contacts) */
  ignoreNewCustomers?: boolean;
  /** Ignore events from existing customers (only sync new CRM contacts) — Pipedrive only */
  ignoreExistingCustomers?: boolean;
  /** Webhook ID(s) for event provider */
  webhookId?: string;

  // Activity Type Mapping (Pipedrive)
  /** Map event type IDs to Pipedrive activity type IDs */
  activityTypeMapping?: Record<string, string>;
  /** Create activities in Pipedrive when events occur (Pipedrive only) */
  createActivities?: boolean;

  /**
   * What to do with the CRM event when a booking is cancelled or rescheduled.
   * 'delete' removes it (default, historical behaviour); 'mark_cancelled' keeps
   * it and flags it as cancelled so the booking history survives in the CRM.
   */
  cancelledEventAction?: 'delete' | 'mark_cancelled';

  // User/Calendar Mapping
  /** Map Calendly member emails or Acuity calendar IDs to CRM user IDs */
  userMappings?: Record<string, string>;

  // Additional Options
  /** Stop processing if record already exists */
  stopIfExists?: boolean;
  /** Target CRM module to create records in (Contacts, Leads, etc.) */
  targetModule?: string;
  /** Enable Signal Notifications in Zoho CRM (Contacts/Leads only) */
  enableSignalNotifications?: boolean;

  // Phone Number Matching
  /** Phone pattern: match numbers without country code prefix */
  phonePatternNoCountryCode?: boolean;
  /** Phone pattern: match numbers starting with 0 */
  phonePatternStartsWith0?: boolean;
  /** Phone pattern: match numbers starting with + */
  phonePatternStartsWithPlus?: boolean;

  // Monday.com–specific
  /** History board ID where incoming events are logged as items (Monday.com "App Template Board") */
  historyModule?: string;
  /** Column ID for the phone field on the contact board (Monday.com) */
  targetPhoneField?: string;
  /** Column ID for the email field on the contact board (Monday.com) */
  targetEmailField?: string;
  /** Multi-board search config — up to 5 boards for searching existing contacts (Monday.com) */
  customSearchModules?: MultiSearchBoardEntry[];
}

// ── WooCommerce Notification Rules ──────────────────────────────────

/** A single notification rule for a WooCommerce order event trigger */
export interface WooCommerceNotificationRule {
  id: string;
  triggerId: string;
  sender: { value: string; label: string; channel: string };
  recipient: { value: string; label: string };
  template: { templateName: string; id?: string; body?: string };
  enabled: boolean;
  /** Filled template variable values (e.g. { "1": "John", "2": "Order #123" }) */
  templateVariables?: Record<string, string>;
  /** Route type — for DLT providers (dlt, manual_dlt) */
  route?: string;
  /** DLT template ID — for manual DLT route */
  templateId?: string;
  /** DLT entity ID — for manual DLT route */
  entityId?: string;
  /** Pre-built TPA-specific message payload for the Cloud Function webhook handler.
   *  Built by tpaModule.getRuleMessagePayload() — CF sends message using this directly. */
  rulePayload?: Record<string, unknown>;
}

// ── Customer opt-out (KAN-155) ──────────────────────────────────────

/** The kinds of send the opt-out footer can be added to. `compose` is every
 *  send from the Compose view, to one recipient or to many. */
export type OptOutFooterTarget = 'workflow' | 'compose' | 'inbox';

/**
 * What a customer's STOP / START does on one sender. It is the admin's choice,
 * not detected: whether a provider answers STOP by itself depends on the number
 * type and the country.
 *   off    nothing — the provider handles opt-outs on that sender
 *   lock   lock the chat, timeline row, block workflows; no reply
 *   reply  the same, plus a confirmation reply to the customer
 */
export type OptOutSenderMode = 'off' | 'lock' | 'reply';

/**
 * Org-level customer opt-out settings — `ua_optout_settings` on the org
 * preferences doc. The server reads the same field (functions/sms/optout.js)
 * on incoming messages and workflow sends, so keep the two shapes in sync.
 * The keywords (STOP, START…) and the footer text are fixed, not settings.
 */
export interface OptOutSettings {
  enabled: boolean;
  /** WhatsApp: also treat the "Stop promotions" button as an opt-out. */
  whatsappStopPromotions: boolean;
  /** Which sends get the "Reply STOP to unsubscribe" footer. */
  footerOn: Record<OptOutFooterTarget, boolean>;
  /** Workflow messages to opted-out customers: false = block, true = send anyway. */
  workflowSendAnyway: boolean;
  /** Per sender, keyed by `optOutSenderKey()`. A sender that is not listed
   *  follows its channel: WhatsApp 'reply', everything else 'lock'. */
  senderModes: Record<string, OptOutSenderMode>;
}
